import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Upload, Link as LinkIcon, Youtube } from "lucide-react";
import { inferSourceFromUrl } from "../lib/videoUtils";
import { toast } from "sonner";

export default function SourceDialog({ open, onOpenChange, onSelect, label }) {
  const fileInputRef = useRef(null);
  const [url, setUrl] = useState("");

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("File must be a video");
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    onSelect({ type: "file", url: objectUrl, name: file.name, file });
    onOpenChange(false);
    toast.success(`Loaded ${file.name} into panel ${label}`);
  };

  const handleUrlSubmit = (e) => {
    e.preventDefault();
    const src = inferSourceFromUrl(url);
    if (!src) {
      toast.error("Invalid URL");
      return;
    }
    onSelect(src);
    onOpenChange(false);
    setUrl("");
    toast.success(`Loaded ${src.type === "youtube" ? "YouTube" : "URL"} into panel ${label}`);
  };

  const pickFile = () => fileInputRef.current?.click();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#121212] border border-white/10 text-white sm:max-w-md"
        data-testid={`source-dialog-${label}`}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase tracking-tight">
            Load video {label}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            Pick a local file, paste a direct video URL, or drop in a YouTube
            link.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 mt-2">
          {/* File upload */}
          <button
            onClick={pickFile}
            className="flex items-center gap-3 p-4 rounded-lg border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] transition-colors text-left"
            data-testid={`pick-file-${label}-button`}
          >
            <div className="w-10 h-10 rounded-md bg-[#007AFF]/15 border border-[#007AFF]/30 flex items-center justify-center shrink-0">
              <Upload className="w-5 h-5 text-[#007AFF]" />
            </div>
            <div>
              <div className="text-sm font-bold uppercase tracking-wider">
                Upload file
              </div>
              <div className="text-xs text-white/50">
                MP4, WebM, MOV — full controls
              </div>
            </div>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFile}
            className="hidden"
            data-testid={`file-input-${label}`}
          />

          {/* URL input */}
          <form
            onSubmit={handleUrlSubmit}
            className="flex flex-col gap-2"
            data-testid={`url-form-${label}`}
          >
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-white/60">
              <LinkIcon className="w-3 h-3" />
              Remote URL or YouTube
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://… or https://youtube.com/watch?v=…"
                className="flex-1 bg-[#1A1A1A] border border-white/20 text-white placeholder-white/30 focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] focus:outline-none rounded-md px-3 py-2 font-mono text-sm"
                data-testid={`url-input-${label}`}
              />
              <button
                type="submit"
                disabled={!url.trim()}
                className="bg-[#007AFF] hover:bg-[#3395FF] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider text-xs rounded-md px-4 transition-colors"
                data-testid={`url-submit-${label}-button`}
              >
                Load
              </button>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-white/40">
              <Youtube className="w-3 h-3" />
              YouTube works — but drawing & frame-step disabled
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
